import type { SiteRecord, PageRecord, SymbolRecord, RevisionDiffResponse, DocumentAST, BlockNode } from './types';

export function adaptSiteRecord(raw: unknown): SiteRecord {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<SiteRecord>;
  return {
    id: String(data.id || ''),
    tenant_id: String(data.tenant_id || ''),
    name: String(data.name || 'Untitled Site'),
    subdomain_slug: String(data.subdomain_slug || ''),
    host_platform: String(data.host_platform || 'helios-coach'),
    active_theme_id: data.active_theme_id ? String(data.active_theme_id) : null,
    published_version_no: Number(data.published_version_no || 0),
    status: String(data.status || 'draft'),
    settings_json: data.settings_json && typeof data.settings_json === 'object' ? data.settings_json : null,
    created_at: String(data.created_at || ''),
    updated_at: String(data.updated_at || ''),
  };
}

export function adaptSiteRecordList(raw: unknown): SiteRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(adaptSiteRecord);
}

export function adaptPageRecord(raw: unknown): PageRecord {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<PageRecord>;

  let parsedDraftAst: DocumentAST | null = null;
  if (data.draft_ast_json && typeof data.draft_ast_json === 'object') {
    parsedDraftAst = data.draft_ast_json as DocumentAST;
  } else if (typeof data.draft_ast_json === 'string' && (data.draft_ast_json as string).trim()) {
    const str = (data.draft_ast_json as string).trim();
    try {
      parsedDraftAst = JSON.parse(str);
    } catch {
      try {
        parsedDraftAst = JSON.parse(atob(str));
      } catch {
        parsedDraftAst = null;
      }
    }
  }

  let parsedSeo: Record<string, unknown> | null = null;
  if (data.seo_json && typeof data.seo_json === 'object') {
    parsedSeo = data.seo_json as Record<string, unknown>;
  } else if (typeof data.seo_json === 'string' && (data.seo_json as string).trim()) {
    const str = (data.seo_json as string).trim();
    try {
      parsedSeo = JSON.parse(str);
    } catch {
      try {
        parsedSeo = JSON.parse(atob(str));
      } catch {
        parsedSeo = null;
      }
    }
  }

  return {
    id: String(data.id || ''),
    tenant_id: String(data.tenant_id || ''),
    site_id: String(data.site_id || ''),
    title: String(data.title || 'Untitled Page'),
    route_path: String(data.route_path || '/'),
    page_type: String(data.page_type || 'landing'),
    draft_ast_json: parsedDraftAst,
    seo_json: parsedSeo,
    status: (data.status === 'published' || data.status === 'archived') ? data.status : 'draft',
    published_revision_id: data.published_revision_id ? String(data.published_revision_id) : null,
    created_by: data.created_by ? String(data.created_by) : undefined,
    updated_at: String(data.updated_at || ''),
  };
}

export function adaptPageRecordList(raw: unknown): PageRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(adaptPageRecord);
}

export function adaptSymbolRecord(raw: unknown): SymbolRecord {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<SymbolRecord>;
  return {
    id: String(data.id || ''),
    tenant_id: String(data.tenant_id || ''),
    site_id: String(data.site_id || ''),
    name: String(data.name || 'Untitled Symbol'),
    symbol_key: String(data.symbol_key || ''),
    category: String(data.category || 'section'),
    symbol_ast: (data.symbol_ast && typeof data.symbol_ast === 'object' ? data.symbol_ast : {}) as BlockNode,
    created_at: String(data.created_at || ''),
    updated_at: String(data.updated_at || ''),
  };
}

export function adaptSymbolRecordList(raw: unknown): SymbolRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(adaptSymbolRecord);
}

export function adaptRevisionDiff(raw: unknown): RevisionDiffResponse {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<RevisionDiffResponse>;
  return {
    version_no: Number(data.version_no || 1),
    published_at: String(data.published_at || ''),
    changes: Array.isArray(data.changes)
      ? data.changes.map((c) => ({
          op: String(c?.op || ''),
          path: String(c?.path || ''),
          description: String(c?.description || ''),
        }))
      : [],
  };
}
